import { useState, useCallback, useEffect } from "react";
import { customBaseUrl, httpGet } from "../../../../store/http";
import {
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Typography,
  Card,
  Table,
} from "antd";
import moment from "moment";
import { parseChannel, channelTagColor } from "../../../AuditTrail/utils";
import AuditDetailModal from "../../../AuditTrail/AuditDetailModal";

const { Title } = Typography;

const statusColors = {
  SUCCESS: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
  FAILED: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e' },
  PENDING: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
};

export const ActivityLogs = ({ id, activeTab }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchActivityLogs = useCallback(async (page = 1, size = 10) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // First try paginated endpoint (backend standard)
      const res = await httpGet(
        `${customBaseUrl.auditUrl}/audit/events/user/${encodeURIComponent(id)}/paginated?page=${page - 1}&size=${size}&sortBy=timestamp&sortDir=desc`
      );
      if (res?.success) {
        const content = res.data?.content || [];
        setLogs(content);
        setPagination({
          current: page,
          pageSize: size,
          total: res.data?.totalElements || content.length,
        });
      } else {
        // Fallback to non-paginated endpoint
        const fallbackRes = await httpGet(
          `${customBaseUrl.auditUrl}/audit/events/user/${encodeURIComponent(id)}`
        );
        if (fallbackRes?.success) {
          const content = Array.isArray(fallbackRes.data) ? fallbackRes.data : (fallbackRes.data?.content || []);
          // Sort client-side desc by timestamp
          content.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setLogs(content.slice((page - 1) * size, page * size));
          setPagination({
            current: page,
            pageSize: size,
            total: content.length,
          });
        } else {
          setError(res?.message || fallbackRes?.message || "Failed to load activity logs.");
        }
      }
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      // Fallback
      try {
        const fallbackRes = await httpGet(
          `${customBaseUrl.auditUrl}/audit/events/user/${encodeURIComponent(id)}`
        );
        if (fallbackRes?.success) {
          const content = Array.isArray(fallbackRes.data) ? fallbackRes.data : (fallbackRes.data?.content || []);
          content.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setLogs(content.slice((page - 1) * size, page * size));
          setPagination({
            current: page,
            pageSize: size,
            total: content.length,
          });
        } else {
          setError("Failed to fetch activity logs.");
        }
      } catch (fallbackErr) {
        setError("An error occurred while fetching activity logs.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === "activity-logs") {
      fetchActivityLogs(1, pagination.pageSize);
    }
  }, [activeTab, fetchActivityLogs, pagination.pageSize]);

  const handleTableChange = (pag) => {
    fetchActivityLogs(pag.current, pag.pageSize);
  };

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type) => (
        <Tag
          style={{
            background: '#fff3eb',
            color: '#ff6700',
            border: '1px solid #ffd8b8',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {type?.replace(/_/g, ' ') || '—'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => <span style={{ fontWeight: 500, color: '#333' }}>{action}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => <span style={{ color: '#595959' }}>{desc || '—'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const sc = statusColors[status] || statusColors.PENDING;
        return (
          <Tag
            style={{
              color: sc.color,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 11,
              padding: '2px 12px',
            }}
          >
            {status || '—'}
          </Tag>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'userAgent',
      key: 'channel',
      render: (ua) => {
        const channel = parseChannel(ua);
        return (
          <Tag
            color={channelTagColor(channel)}
            style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}
          >
            {channel}
          </Tag>
        );
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip) => (
        <span style={{ fontFamily: 'monospace', color: '#595959', fontSize: 12 }}>
          {ip || '—'}
        </span>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 12,
        padding: 20,
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            Activity Logs
          </Title>
        </Col>
      </Row>

      <div style={{ marginTop: 12 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin tip="Loading activity logs..." />
          </div>
        ) : error ? (
          <Alert
            message="Error loading activity logs"
            description={error}
            type="error"
            showIcon
          />
        ) : logs.length > 0 ? (
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
            }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
            No activity logs found for this user.
          </div>
        )}
      </div>

      <AuditDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedLog}
      />
    </Card>
  );
};
